"use client";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Sparkles, Wallet } from "lucide-react";
import { Dialog, DialogHeader, DialogBody, DialogFooter, DialogCloseButton } from "@speakai/ui/dialog";
import { Button } from "@speakai/ui/button";
import { Badge } from "@speakai/ui/badge";
import { cn } from "@/lib/utils/cn";
import { useAppStore } from "@/stores";
import { useAccountUsage } from "@/features/dashboard/queries";
import { usePermissions } from "@/hooks/use-permissions";
import { PaywallCustomPath } from "@/features/booking/components/paywall-custom-path";
import { PricingPage } from "@/features/pricing/pricing-page";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { getPaywallState, PAYWALL_COPY } from "../lib/paywall-content";
import { usePaywallAtIntent } from "../hooks/use-paywall-at-intent";
import { usePricingPlans } from "../hooks/use-pricing-plans";
import type { SubscriptionGateSource } from "../store";
import { CreateSubscription } from "./create-subscription";

const KEEP_GOING_SOURCES: ReadonlySet<SubscriptionGateSource> = new Set<SubscriptionGateSource>([
  "chat_pending_payment",
  "upload_balance_zero",
  "media_pending_payment",
  "translate_pending_payment",
  "meeting_bot_out_of_credits",
  "expired_account_action",
]);
const KEEP_GOING_TOP_UP_USD = 20;

export function SubscriptionGateDialog() {
  const gateOpen = useAppStore((s) => s.gateOpen);
  const gatePayload = useAppStore((s) => s.gatePayload);
  const closeSubscriptionGate = useAppStore((s) => s.closeSubscriptionGate);
  const { canManageBilling } = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const openAddBalance = useAppStore((s) => s.openAddBalance);
  const { data: subscription } = useAccountUsage();
  const paywallState = getPaywallState(subscription);
  const copyKeys = PAYWALL_COPY[paywallState];
  const { keepGoing, arm } = usePaywallAtIntent();
  const { proMonthly, DURATION_MONTHLY } = usePricingPlans(1);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [choice, setChoice] = useState<"pro" | "top_up">("pro");
  const proOptionRef = useRef<HTMLButtonElement>(null);
  const topUpOptionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (gateOpen) setChoice("pro");
  }, [gateOpen]);

  useEffect(() => {
    if (gateOpen && gatePayload) {
      trackFunnel("paywall", "view", {
        source: gatePayload.source,
        utm_source: gatePayload.utmSource?.slice(0, 80),
      });
    }
  }, [gateOpen, gatePayload?.source, gatePayload?.utmSource]);

  if (!gateOpen || !gatePayload) return null;
  // Avoid the flicker between default "Upgrade" copy and the correct state
  // copy while subscription is still hydrating.
  if (!subscription) return null;

  const handleClose = () => {
    trackFunnel("paywall", "close_click", { source: gatePayload.source });
    closeSubscriptionGate();
  };

  // ── Two-option variant: expired account (control) and every credit block in the keep_going arm. ──
  const isKeepGoing = keepGoing && KEEP_GOING_SOURCES.has(gatePayload.source);
  if (isKeepGoing || gatePayload.source === "expired_account_action") {
    const { source, onResume } = gatePayload;
    const triggeredFrom = gatePayload.utmSource?.slice(0, 80) ?? "";
    const topUpAmount = Math.max(KEEP_GOING_TOP_UP_USD, Math.ceil(gatePayload.topUpAmount ?? 0));

    const handleSubscribe = () => {
      trackFunnel("paywall", "subscribe_click", { source, triggered_from: triggeredFrom, ...(isKeepGoing ? { arm } : {}) });
      if (isKeepGoing) {
        setCheckoutOpen(true);
        return;
      }
      closeSubscriptionGate();
      navigate("/pricing");
    };

    const handleAddBalance = () => {
      trackFunnel("paywall", "add_balance_click", { source, triggered_from: triggeredFrom, ...(isKeepGoing ? { arm } : {}) });
      closeSubscriptionGate();
      if (isKeepGoing) {
        openAddBalance({ amount: topUpAmount, source, onSuccess: onResume });
        return;
      }
      openAddBalance();
    };

    const handleSubscribed = () => {
      trackFunnel("paywall", "subscribed", { source, arm });
      setCheckoutOpen(false);
      closeSubscriptionGate();
      onResume?.();
    };

    const title = isKeepGoing
      ? t("PAYWALL.KEEP_GOING.TITLE", "Keep going with Speak")
      : t(PAYWALL_COPY.expired.titleKey);
    const desc = !isKeepGoing
      ? t(PAYWALL_COPY.expired.descKey)
      : source === "expired_account_action"
        ? t("PAYWALL.KEEP_GOING.DESC_EXPIRED", "Your free trial has ended. Your files and chat history are saved.")
        : t("PAYWALL.KEEP_GOING.DESC", "You've used your free credit. Your files and chat history are saved.");
    const proPrice = proMonthly
      ? `$${proMonthly.discountedCost}${t("SPECIAL_OFFERS.PER_MONTH", "/month")}`
      : "";
    const selected = isKeepGoing && !proMonthly ? "top_up" : choice;
    const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if ((e.key !== "ArrowDown" && e.key !== "ArrowUp") || !proMonthly) return;
      e.preventDefault();
      const next = selected === "pro" ? "top_up" : "pro";
      setChoice(next);
      (next === "pro" ? proOptionRef : topUpOptionRef).current?.focus();
    };
    const optionClass = (isSelected: boolean) =>
      cn(
        "flex w-full items-center justify-between gap-3 rounded-md border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isSelected ? "border-primary bg-primary/5" : "hover:border-muted-foreground/30",
      );

    return (
      <>
        <Dialog
          data-testid="expired-paywall-dialog"
          aria-labelledby="gate-dialog-title"
          open={gateOpen && !checkoutOpen}
          // Dialog's onClose fires on backdrop click and Escape; this offer closes only via the X or Cancel.
          onClose={() => {}}
          size={isKeepGoing ? "default" : "sm"}
        >
          <DialogHeader>
            <div>
              <h2 id="gate-dialog-title" className="text-lg font-semibold">
                {title}
              </h2>
              {isKeepGoing && <p className="mt-1 text-sm text-muted-foreground">{desc}</p>}
            </div>
            <DialogCloseButton onClose={handleClose} />
          </DialogHeader>

          <DialogBody className="space-y-4">
            {!isKeepGoing && <p className="text-sm text-muted-foreground">{desc}</p>}
            {isKeepGoing && (
              <>
              {canManageBilling ? (
                <div className="flex flex-col gap-3" role="radiogroup" aria-labelledby="gate-dialog-title">
                  {proMonthly && (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selected === "pro"}
                      tabIndex={selected === "pro" ? 0 : -1}
                      ref={proOptionRef}
                      onKeyDown={handleOptionKeyDown}
                      onClick={() => setChoice("pro")}
                      className={optionClass(selected === "pro")}
                      data-testid="keep-going-option-pro"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10" aria-hidden="true">
                          <Sparkles className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="flex items-center gap-2 text-sm font-medium">
                            {proMonthly.displayName}
                            <Badge variant="default" className="text-[10px]">
                              {t("PAYWALL.KEEP_GOING.RECOMMENDED", "Recommended")}
                            </Badge>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("PAYWALL.KEEP_GOING.PRO_DESC", "Monthly credits for transcription, AI chat and the meeting bot.")}{" "}
                            {t("PRICING.CANCEL_ANYTIME", "Cancel anytime")}.
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <p className="text-sm font-semibold">{proPrice}</p>
                        {selected === "pro" && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
                      </div>
                    </button>
                  )}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected === "top_up"}
                    tabIndex={selected === "top_up" ? 0 : -1}
                    ref={topUpOptionRef}
                    onKeyDown={handleOptionKeyDown}
                    onClick={() => setChoice("top_up")}
                    className={optionClass(selected === "top_up")}
                    data-testid="keep-going-option-top-up"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10" aria-hidden="true">
                        <Wallet className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{t("PAYWALL.KEEP_GOING.TOP_UP_TITLE", "Add credit")}</p>
                        <p className="text-xs text-muted-foreground">
                          {t("PAYWALL.KEEP_GOING.TOP_UP_DESC", "Pay as you go. No subscription.")}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <p className="text-sm font-semibold">${topUpAmount}</p>
                      {selected === "top_up" && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
                    </div>
                  </button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="keep-going-no-billing">
                  {t("PAYMENT.NO_BILLING_PERMISSION", "Contact your account admin to manage billing.")}
                </p>
              )}
              </>
            )}
            <PaywallCustomPath />
          </DialogBody>

          {(!isKeepGoing || canManageBilling) && (
            <DialogFooter>
              {isKeepGoing ? (
                <>
                  <Button type="button" variant="outline" onClick={handleClose}>
                    {t("ACTION.CANCEL", "Cancel")}
                  </Button>
                  <Button
                    type="button"
                    onClick={selected === "pro" ? handleSubscribe : handleAddBalance}
                    data-testid="keep-going-continue"
                  >
                    {selected === "pro"
                      ? `${t("PRICING.ACTION.START_PRO", "Start Pro")}: ${proPrice}`
                      : t("PAYWALL.KEEP_GOING.TOP_UP_CTA", "Add ${{amount}}", { amount: topUpAmount })}
                  </Button>
                </>
              ) : (
                <>
                  {canManageBilling && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddBalance}
                      aria-label={t("PAYWALL.EXPIRED_ACTION_ADD_BALANCE", "Add Credit Instead")}
                      data-testid="expired-paywall-add-balance"
                    >
                      {t("PAYWALL.EXPIRED_ACTION_ADD_BALANCE", "Add Credit Instead")}
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={handleSubscribe}
                    aria-label={t("PAYWALL.EXPIRED_ACTION_SUBSCRIBE", "Resume Subscription")}
                    data-testid="expired-paywall-subscribe-now"
                  >
                    {t("PAYWALL.EXPIRED_ACTION_SUBSCRIBE", "Resume Subscription")}
                  </Button>
                </>
              )}
            </DialogFooter>
          )}
        </Dialog>
        {checkoutOpen && proMonthly && (
          <CreateSubscription
            open={checkoutOpen}
            plan={proMonthly}
            duration={DURATION_MONTHLY}
            teamMembers={1}
            onClose={() => setCheckoutOpen(false)}
            onSuccess={handleSubscribed}
            offerTracking={{ offerArm: arm }}
          />
        )}
      </>
    );
  }

  // ── Default variant: full PricingPage embedded with header + X close. ──

  // Resolve title / description with payload-key override, otherwise state-based default.
  const resolvedTitle = gatePayload.titleKey ? t(gatePayload.titleKey) : t(copyKeys.titleKey);
  const resolvedDesc = gatePayload.descKey ? t(gatePayload.descKey) : t(copyKeys.descKey);

  const handleCheckoutSuccess = () => {
    trackFunnel("paywall", "subscribed", { source: gatePayload.source });
    const { onResume } = gatePayload;
    closeSubscriptionGate();
    onResume?.();
  };

  return (
    <Dialog
      data-testid="subscription-gate-dialog"
      aria-labelledby="gate-dialog-title"
      open={gateOpen}
      onClose={handleClose}
      size="full"
    >
      <DialogHeader>
        <div className="flex flex-col items-start gap-1">
          <h2 id="gate-dialog-title" className="text-lg font-semibold">
            {resolvedTitle}
          </h2>
          <p className="text-sm text-muted-foreground">{resolvedDesc}</p>
        </div>
        <DialogCloseButton onClose={handleClose} />
      </DialogHeader>

      <div className="max-h-[calc(100vh-140px)] overflow-y-auto">
        <PricingPage onCheckoutSuccess={handleCheckoutSuccess} />
      </div>
    </Dialog>
  );
}
